import { useState, useEffect } from 'react';
import {
  Trophy,
  Medal,
  Award,
  Download,
  Calendar,
  Settings,
  GripVertical,
  Save,
} from 'lucide-react';
import {
  performanceService,
  AgentPerformanceRating as AgentRating,
  TimeWindow,
} from '../../services/performance.service';
import { useAuthStore } from '../../stores/auth.store';
import MetricWeightsModal from './MetricWeightsModal';

export default function AgentPerformanceRating() {
  const user = useAuthStore((state) => state.user);
  const [ratings, setRatings] = useState<AgentRating[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('this_week');
  const [teamFilter] = useState<string | undefined>(undefined);
  const [isWeightsModalOpen, setIsWeightsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [editedRankings, setEditedRankings] = useState<AgentRating[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    loadRatings();
  }, [user?.organizationId, timeWindow, teamFilter]);

  const loadRatings = async () => {
    if (!user?.organizationId) return;

    setIsLoading(true);
    try {
      const data = await performanceService.getAgentPerformanceRatings(
        user.organizationId,
        timeWindow,
        teamFilter
      );
      setRatings(data);
      setEditedRankings(data);
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to load agent performance ratings:', error);
      // Use demo data instead of showing error
      const demoData = getDemoAgentRatings();
      setRatings(demoData);
      setEditedRankings(demoData);
      setIsEditMode(false);
    } finally {
      setIsLoading(false);
    }
  };

  const getDemoAgentRatings = (): AgentRating[] => {
    const metricWeights = {
      csat: 0.50,
      qaRating: 0.20,
      wfmAdherence: 0.10,
      occupancy: 0.10,
      productivity: 0.10,
    };

    return [
      {
        id: 'demo-rating-001',
        agentId: 'agent-001',
        agentName: 'Sarah Chen',
        teamId: 'team-001',
        teamName: 'Phone Support',
        overallScore: 92.3,
        rank: 1,
        metricScores: {
          wfmAdherence: 95.2,
          occupancy: 88.5,
          productivity: 94.8,
          csat: 96.5,
          sla: 93.2,
          costPerContact: 87.0,
          qaRating: 91.5,
        },
        metricWeights,
      },
      {
        id: 'demo-rating-002',
        agentId: 'agent-002',
        agentName: 'Marcus Johnson',
        teamId: 'team-002',
        teamName: 'Technical Support',
        overallScore: 89.7,
        rank: 2,
        metricScores: {
          wfmAdherence: 92.8,
          occupancy: 85.3,
          productivity: 91.2,
          csat: 93.8,
          sla: 88.5,
          costPerContact: 84.2,
          qaRating: 89.0,
        },
        metricWeights,
      },
      {
        id: 'demo-rating-003',
        agentId: 'agent-003',
        agentName: 'Emily Rodriguez',
        teamId: 'team-001',
        teamName: 'Phone Support',
        overallScore: 87.4,
        rank: 3,
        metricScores: {
          wfmAdherence: 88.5,
          occupancy: 91.2,
          productivity: 89.3,
          csat: 90.5,
          sla: 85.8,
          costPerContact: 82.5,
          qaRating: 86.5,
        },
        metricWeights,
      },
      {
        id: 'demo-rating-004',
        agentId: 'agent-004',
        agentName: 'David Kim',
        teamId: 'team-003',
        teamName: 'Live Chat',
        overallScore: 85.2,
        rank: 4,
        metricScores: {
          wfmAdherence: 86.3,
          occupancy: 87.8,
          productivity: 86.5,
          csat: 88.2,
          sla: 83.5,
          costPerContact: 80.8,
          qaRating: 84.0,
        },
        metricWeights,
      },
      {
        id: 'demo-rating-005',
        agentId: 'agent-005',
        agentName: 'Jessica Taylor',
        teamId: 'team-002',
        teamName: 'Technical Support',
        overallScore: 83.8,
        rank: 5,
        metricScores: {
          wfmAdherence: 84.2,
          occupancy: 85.5,
          productivity: 84.8,
          csat: 86.5,
          sla: 81.2,
          costPerContact: 79.5,
          qaRating: 82.5,
        },
        metricWeights,
      },
      {
        id: 'demo-rating-006',
        agentId: 'agent-006',
        agentName: 'Michael Brown',
        teamId: 'team-004',
        teamName: 'Email Support',
        overallScore: 81.5,
        rank: 6,
        metricScores: {
          wfmAdherence: 82.5,
          occupancy: 83.2,
          productivity: 82.3,
          csat: 84.8,
          sla: 79.5,
          costPerContact: 77.2,
          qaRating: 80.0,
        },
        metricWeights,
      },
      {
        id: 'demo-rating-007',
        agentId: 'agent-007',
        agentName: 'Aisha Patel',
        teamId: 'team-003',
        teamName: 'Live Chat',
        overallScore: 79.3,
        rank: 7,
        metricScores: {
          wfmAdherence: 80.5,
          occupancy: 81.8,
          productivity: 80.2,
          csat: 82.5,
          sla: 77.8,
          costPerContact: 75.5,
          qaRating: 78.5,
        },
        metricWeights,
      },
      {
        id: 'demo-rating-008',
        agentId: 'agent-008',
        agentName: 'James Wilson',
        teamId: 'team-001',
        teamName: 'Phone Support',
        overallScore: 77.8,
        rank: 8,
        metricScores: {
          wfmAdherence: 78.8,
          occupancy: 79.5,
          productivity: 78.5,
          csat: 80.2,
          sla: 76.2,
          costPerContact: 74.0,
          qaRating: 76.8,
        },
        metricWeights,
      },
      {
        id: 'demo-rating-009',
        agentId: 'agent-009',
        agentName: 'Sophia Lee',
        teamId: 'team-004',
        teamName: 'Email Support',
        overallScore: 75.2,
        rank: 9,
        metricScores: {
          wfmAdherence: 76.5,
          occupancy: 77.2,
          productivity: 76.8,
          csat: 78.5,
          sla: 73.8,
          costPerContact: 72.5,
          qaRating: 74.5,
        },
        metricWeights,
      },
      {
        id: 'demo-rating-010',
        agentId: 'agent-010',
        agentName: 'Ryan Martinez',
        teamId: 'team-005',
        teamName: 'Social Media',
        overallScore: 72.5,
        rank: 10,
        metricScores: {
          wfmAdherence: 74.2,
          occupancy: 75.8,
          productivity: 73.5,
          csat: 76.2,
          sla: 70.5,
          costPerContact: 69.8,
          qaRating: 72.0,
        },
        metricWeights,
      },
    ];
  };

  const handleExportCSV = () => {
    performanceService.exportAgentRatingsToCSV(ratings);
  };

  const handleDragStart = (index: number) => {
    setIsDragging(true);
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newRankings = [...editedRankings];
    const draggedItem = newRankings[draggedIndex];

    newRankings.splice(draggedIndex, 1);
    newRankings.splice(index, 0, draggedItem);

    // Update ranks
    newRankings.forEach((item, idx) => {
      item.rank = idx + 1;
    });

    setEditedRankings(newRankings);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedIndex(null);
  };

  const handleSaveRankings = async () => {
    if (!user?.organizationId) return;

    try {
      const rankings = editedRankings.map((rating) => ({
        agentId: rating.agentId,
        rank: rating.rank,
      }));

      await performanceService.updateAgentRanking(user.organizationId, rankings);
      await loadRatings();
    } catch (error) {
      console.error('Failed to save rankings:', error);
      alert('Failed to save rankings. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditedRankings(ratings);
    setIsEditMode(false);
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />;
      case 3:
        return <Award className="h-6 w-6 text-orange-600" />;
      default:
        return <span className="text-lg font-bold text-gray-600">#{rank}</span>;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 80) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (score >= 70) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const timeWindowOptions: Array<{ value: TimeWindow; label: string }> = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
  ];

  const displayRatings = isEditMode ? editedRankings : ratings;

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading agent ratings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Agent Performance Ratings</h2>
            <p className="text-gray-600 mt-1">
              Ranked performance across all key metrics
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsWeightsModalOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Settings className="h-4 w-4" />
              <span>Metric Weights</span>
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Time Window
            </label>
            <select
              value={timeWindow}
              onChange={(e) => setTimeWindow(e.target.value as TimeWindow)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {timeWindowOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Edit Mode Controls */}
        {isEditMode && (
          <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-2 text-purple-700">
              <GripVertical className="h-5 w-5" />
              <span className="font-medium">Drag and drop to reorder rankings</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRankings}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Save className="h-4 w-4" />
                <span>Save Rankings</span>
              </button>
            </div>
          </div>
        )}

        {!isEditMode && (
          <div className="mt-4">
            <button
              onClick={() => setIsEditMode(true)}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              Edit Rankings
            </button>
          </div>
        )}
      </div>

      {/* Ratings List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {displayRatings.length === 0 ? (
          <div className="p-12 text-center">
            <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No ratings available</h3>
            <p className="text-gray-600">
              Performance ratings will appear here once agent data is collected
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {displayRatings.map((rating, index) => (
              <div
                key={rating.id}
                draggable={isEditMode}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`p-6 hover:bg-gray-50 transition-colors ${
                  isEditMode ? 'cursor-move' : ''
                } ${isDragging && draggedIndex === index ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start space-x-6">
                  {/* Rank */}
                  <div className="flex-shrink-0 w-12 flex items-center justify-center">
                    {isEditMode && <GripVertical className="h-5 w-5 text-gray-400 mr-2" />}
                    {getRankIcon(rating.rank)}
                  </div>

                  {/* Agent Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{rating.agentName}</h3>
                        {rating.teamName && (
                          <p className="text-sm text-gray-600">{rating.teamName}</p>
                        )}
                      </div>
                      <div className={`px-4 py-2 rounded-lg border ${getScoreColor(rating.overallScore)}`}>
                        <div className="text-center">
                          <p className="text-xs font-medium mb-1">Overall Score</p>
                          <p className="text-2xl font-bold">{rating.overallScore.toFixed(1)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Metric Scores */}
                    <div className="grid grid-cols-6 gap-3">
                         <MetricBadge
                        label="CSAT"
                        score={rating.metricScores.csat}
                        weight={rating.metricWeights.csat}
                      />
                      <MetricBadge
                        label="QA"
                        score={rating.metricScores.qaRating}
                        weight={rating.metricWeights.qaRating}
                      />
                      <MetricBadge
                        label="WFM"
                        score={rating.metricScores.wfmAdherence}
                        weight={rating.metricWeights.wfmAdherence}
                      />
                      <MetricBadge
                        label="Occupancy"
                        score={rating.metricScores.occupancy}
                        weight={rating.metricWeights.occupancy}
                      />
                      <MetricBadge
                        label="Productivity"
                        score={rating.metricScores.productivity}
                        weight={rating.metricWeights.productivity}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Metric Weights Modal */}
      {isWeightsModalOpen && user?.organizationId && (
        <MetricWeightsModal
          organizationId={user.organizationId}
          currentWeights={ratings[0]?.metricWeights}
          onClose={() => setIsWeightsModalOpen(false)}
          onSuccess={() => {
            setIsWeightsModalOpen(false);
            loadRatings();
          }}
        />
      )}
    </div>
  );
}

interface MetricBadgeProps {
  label: string;
  score: number;
  weight: number;
}

function MetricBadge({ label, score, weight }: MetricBadgeProps) {
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'bg-green-50 text-green-700 border-green-200';
    if (score >= 80) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (score >= 70) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  return (
    <div className={`p-2 rounded border ${getScoreColor(score)}`}>
      <p className="text-xs font-medium mb-1 truncate">{label}</p>
      <p className="text-lg font-bold">{score.toFixed(0)}</p>
      <p className="text-xs opacity-75">{(weight * 100).toFixed(0)}%</p>
    </div>
  );
}
